import type { PropsWithChildren } from "react";
import { Link } from "react-router-dom-v5-compat";
import { availabilityTokenCache } from "@b2bportal/core-hotels";
import {
  HotelNavigationUtility,
  useDeviceType,
} from "@b2bportal/core-utilities";
import type { GuestsSelection, Lodging } from "@b2bportal/lodging-api";
import { HOTELS_SHOP_PATH } from "@overrides/utilities";
import {
  HOTEL_DEALS_OFFER_ID,
  HOTEL_DEALS_OFFER_NAME,
  OFFER_SOURCE_PARAM,
  OfferFunnelEntrySource,
  OfferRawEventName,
  OfferScreen,
  OfferType,
  useEmitRbcRawEvent,
} from "@/tracking/offers";
import { forwardUtmParams } from "@/tracking/utmParams";

export interface OfferHotelLinkProps {
  hotel: Lodging;
  dateRange: { from: string; until: string };
  guests: GuestsSelection;
  rooms: number;
  className?: string;
}

/**
 * Links an offer hotel card to its shop page, mirroring the stays LinkToHotel:
 * builds the shop URL and caches the hotel's shop token so the shop page loads
 * this offer without re-fetching. Dates/guests/rooms come from props, not the
 * availability Redux slice the offers page never populates.
 */
export const OfferHotelLink = ({
  hotel,
  dateRange,
  guests,
  rooms,
  className,
  children,
}: PropsWithChildren<OfferHotelLinkProps>) => {
  const { isDesktopAndUp } = useDeviceType();

  const emitTappedListCard = useEmitRbcRawEvent(
    OfferRawEventName.HotelTappedListCard
  );

  const { id, name } = hotel.lodging;
  const fromDate = HotelNavigationUtility.formatDateForUrl(dateRange.from);
  const untilDate = HotelNavigationUtility.formatDateForUrl(dateRange.until);

  // Name is optional for the shop; fall back to "_" if it has no URL slug.
  const urlFriendlyName = HotelNavigationUtility.placeLabelToUrl(name) || "_";
  // The shop opens in a new tab on desktop, which can't read this tab's
  // sessionStorage — carry the offer source and entry UTMs on the URL.
  const params = forwardUtmParams(
    HotelNavigationUtility.buildSearchParams({
      dateRange,
      guests,
      roomsCount: rooms,
    })
  );
  params.set(OFFER_SOURCE_PARAM, OfferFunnelEntrySource.offer_page);
  const target = `${HOTELS_SHOP_PATH}/${urlFriendlyName}/${id}?${params.toString()}`;

  const shopToken = hotel.price?.opaqueShopRequest;

  // Cache the token under the key the shop page reads, on every interaction path
  // (hover/focus/touch/click) so it's stored however the link is opened.
  const storeHotelToken = () => {
    if (shopToken == null) return;
    availabilityTokenCache.set(
      { lodgingId: id, fromDate, untilDate, guests, rooms },
      shopToken
    );
  };

  const linkProps = isDesktopAndUp
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};

  const handleClick = () => {
    storeHotelToken();
    emitTappedListCard({
      properties: {
        screen: OfferScreen.offers_page,
        offer_type: OfferType.hotel_merch,
        offer_id: HOTEL_DEALS_OFFER_ID,
        offer_name: HOTEL_DEALS_OFFER_NAME,
        is_claimable: false,
      },
    });
  };

  return (
    <Link
      to={target}
      className={className}
      onFocus={storeHotelToken}
      onMouseOver={storeHotelToken}
      onTouchStart={storeHotelToken}
      onClick={handleClick}
      {...linkProps}
    >
      {children}
    </Link>
  );
};
